/**
 * EditModeContext — the per-view edit stance (WYSIWYG toggle).
 *
 * Edit mode is a transient interaction STANCE, not document content: view mode
 * = click-to-navigate/select/dive; edit mode = click-to-select + reveal inline
 * edits (double-click still dives; navigation moves to modifier/context menu).
 * All WYSIWYG affordances gate behind `editMode && canEdit`.
 *
 * It is PER-VIEW, not global — each open tab owns its own stance (a board tab
 * can be editing while a process tab beside it is just viewing). The provider
 * keys the flag by the active tab id (see EditModeProvider) so switching tabs
 * restores each view's own stance; the whole map is session-ephemeral (like
 * {@link QuietDiagnosticsContext} / {@link ActiveViewContext}) — reopening resets to
 * viewing. `canEdit` is orthogonal (are mutations wired at all); surfaces gate
 * on `editMode && canEdit`, and the toggle only renders when `canEdit`.
 */
import { createContext, useContext } from "react";

export interface EditModeApi {
  /** Whether the ACTIVE view is currently in edit mode. */
  readonly editMode: boolean;
  /** Set the active view's stance. */
  readonly setEditMode: (on: boolean) => void;
  /** Flip the active view's stance. */
  readonly toggle: () => void;
}

export const EditModeContext = createContext<EditModeApi | null>(null);

/** The active view's edit stance + setters. Throws outside a provider. */
export function useEditMode(): EditModeApi {
  const m = useContext(EditModeContext);
  if (!m) throw new Error("useEditMode must be used within an <EditModeProvider>");
  return m;
}

/** Read-only, optional — for surfaces that may mount without the provider
 *  (Studio host, isolated tests): absent → never editing. */
export function useOptionalEditMode(): boolean {
  return useContext(EditModeContext)?.editMode ?? false;
}
